import { AfterViewInit, Component, computed, DestroyRef, effect, ElementRef, inject, input, output, signal, untracked } from '@angular/core';
import { AuthService } from '../../../../core/services/auth.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PropertyLikeService } from '../../services/property-like.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DialogService } from '../../../../shared/services/dialog.service';
import { HapticsService } from '../../../../shared/general/services/haptics.service';
import { SnackBarService } from '../../../../shared/services/snackbar.service';

@Component({
  selector: 'app-save-button',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule
  ],
  standalone: true,
  templateUrl: './save-button.html',
  styleUrl: './save-button.scss',
})
export class SaveButtonComponent implements AfterViewInit {
  private haptics = inject(HapticsService);

  private likeService = inject(PropertyLikeService);
  private authService = inject(AuthService);
  private dialog = inject(DialogService);
  private el = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private snackbar = inject(SnackBarService);
  
  isParentDark = signal(true);
  theme = input<'light' | 'dark'>('dark');
  
  constructor() {
    effect(() => {
      const id = this.propertyId();
      const currentInput = this.isLiked();
      //console.log('init', id, currentInput);
      this.authService.loggedInUser$.subscribe(user => {
        if(user) {
          if (id && currentInput === undefined) {
            untracked(() => this.fetchInitialStatus(id));
          }
        } 
      });
    });
  }
  ngAfterViewInit(): void {
    const parent = this.el.nativeElement.parentElement;
    const bgColor = window.getComputedStyle(parent).backgroundColor;
    this.isParentDark.set(this.isColorDark(bgColor));
  }
  
  private isColorDark(color: string): boolean {
    const rgb = color.match(/\d+/g);
    if (!rgb) return true;
    const [r, g, b] = rgb.map(Number);
    // Perceptual luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  }

  private fetchInitialStatus(id: string) {
    this.isPending.set(true);
    //console.log('initial status');
    this.likeService.getLikeStatus(id).pipe(
      takeUntilDestroyed(this.destroyRef)
  ).subscribe({
      next: (liked: boolean) => {
        //console.log('liked', liked);
        this.localLiked.set(liked);
        this.likedChange.emit(liked);
        //console.log('initial status');
        this.isPending.set(false);
      },
      error: () => {
        //console.log('fetchinitial error');
        this.isPending.set(false);
      } 
    });
  }
  // Inputs
  propertyId = input.required<string>();
  isLiked = input.required<boolean>();
  isAnimating = signal(false);
  // Output
  likedChange = output<boolean>(); 
  private user = toSignal(this.authService.user$);
  isPending = signal(false); 

  private localLiked = signal<boolean | null>(null);
  
  displayLiked = computed(() => {
    const local = this.localLiked();
    return local !== null ? local : this.isLiked();
  });

  toggleLike(event: MouseEvent) {
    console.log('toggle like 1');
    this.haptics.success();
    event.preventDefault();
    event.stopPropagation();
    
    if (!this.authService.isAuthenticatedSnapshot) {
      this.snackbar.open('You must be signed in to save properties.', ' ', 
            { 
              duration: 5000, 
              panelClass: ['horizinns-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
    } else {
    const userId = this.user()?.id;
    if (!userId) {
      this.dialog.alert('You must be logged in to save this property.', '', 'Ok');
    }
    if (!userId || this.isPending()) return;

    const newState = !this.displayLiked();
    
    this.localLiked.set(newState);
    this.likedChange.emit(newState);
    this.isPending.set(true);

    this.likeService.toggleLike(this.propertyId())
      .subscribe({
        next: (res: any) => {
          this.isPending.set(false);
          const serverState = res.action === 'liked';
          this.likedChange.emit(serverState);
          this.localLiked.set(null);
          this.isAnimating.set(true);
          setTimeout(() => this.isAnimating.set(false), 10000);
        },
        error: () => {
          this.isPending.set(false);
          this.localLiked.set(null);
          this.likedChange.emit(this.isLiked());
        }
      });
    }
  }
}
